import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, kaspreadsheetUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-xl border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Left: Brand */}
          <Link to="/" className="flex flex-col group">
            <span className="text-lg md:text-xl font-bold text-foreground-900 font-heading tracking-tight whitespace-nowrap">
              GTBuy<span className="text-primary-500">Spreadsheet</span>
            </span>
            <span className="text-[10px] text-foreground-400 font-medium tracking-widest uppercase whitespace-nowrap">
              Smart Sourcing Tools
            </span>
          </Link>

          {/* Center: Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <span className={`w-3.5 h-3.5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[560px] bg-white rounded-2xl border border-background-200/70 overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="p-3 grid grid-cols-2 gap-1 max-h-[400px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors duration-150 group/item"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 group-hover/item:bg-primary-100 transition-colors duration-150">
                          <i className={`${cat.icon} text-foreground-600 group-hover/item:text-primary-600 text-sm`}></i>
                        </span>
                        <span className="text-sm font-medium text-foreground-800 group-hover/item:text-foreground-950 whitespace-nowrap">
                          {cat.name}
                        </span>
                        <span className="ml-auto w-4 h-4 flex items-center justify-center opacity-0 group-hover/item:opacity-100 transition-opacity duration-150">
                          <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/guide/gtbuy-sheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Guide
            </Link>

            <Link
              to="/guide/gtbuy-sheet-guide"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-full hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Shopping Guide
            </Link>
          </div>

          {/* Right: CTA Buttons */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-foreground-800 bg-background-100 hover:bg-background-200 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              gtb<span className="text-primary-500">u</span>y sheet
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
            <a
              href={kaspreadsheetUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-lg text-foreground-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/60 animate-[fadeIn_0.15s_ease-out] max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                <i className="ri-home-line text-foreground-600 text-xs"></i>
              </span>
              Home
            </Link>

            {/* Mobile Categories Accordion */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 whitespace-nowrap"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-grid-line text-foreground-600 text-xs"></i>
                  </span>
                  14 Categories
                </div>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm text-foreground-400"></i>
                </span>
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-3 pb-2 animate-[fadeIn_0.15s_ease-out]">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-2 py-2.5 rounded-xl hover:bg-background-100 transition-colors"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-background-100 flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-500 text-[10px]`}></i>
                      </span>
                      <span className="text-xs text-foreground-700 whitespace-nowrap leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guide/gtbuy-sheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                <i className="ri-article-line text-foreground-600 text-xs"></i>
              </span>
              Guide
            </Link>

            <Link
              to="/guide/gtbuy-sheet-guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 whitespace-nowrap"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100">
                <i className="ri-shopping-bag-3-line text-foreground-600 text-xs"></i>
              </span>
              Shopping Guide
            </Link>

            {/* Mobile CTA */}
            <div className="pt-3 px-3 border-t border-background-200/60">
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-semibold text-white bg-foreground-900 rounded-full transition-all duration-200 whitespace-nowrap"
              >
                gtb<span className="text-primary-400">u</span>y sheet
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}